import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Upload,
  FileText,
  Image,
  ImageIcon,
  Trash2,
  Loader2,
} from 'lucide-react';
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Navigate } from 'react-router-dom';
import createApiClient from '@/api/axiosInstance';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import UploadDocumentModal from './modals/UploadDocumentModal';

const FileUpload = ({
  selectedPart,
  open,
  onClose,
  DOCUMENT_TITLES,
  MAX_FILE_SIZE,
  ACCEPTED_FILE_TYPES,
  id,
  label,
  documentType,
  existingDocument,
  existingDocuments,
  setExistingDocuments,
  documentStates,
  setDocumentStates,
  uploadProgress,
  setUploadProgress,
  isUploading,
  setIsUploading,
  setError,
  editState = true,
  classNames = 'w-48 h-28',
  isUploadingCertificate = false,
}) => {
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const dispatch = useDispatch();
  const apiClient = createApiClient(Navigate, dispatch);

  const file = documentStates[documentType];

  const renderFilePreview = (fileToPreview) => {
    if (fileToPreview?.type?.startsWith('image/')) {
      return <Image className="h-8 w-8 text-gray-400" />;
    } else if (fileToPreview?.type === 'application/pdf') {
      return <FileText className="h-8 w-8 text-gray-400" />;
    } else {
      return <ImageIcon className="h-8 w-8 text-gray-400" />;
    }
  };

  const fetchExistingDocuments = async () => {
    try {
      const response = await apiClient.get('/api/documents/get');
      if (response.status === 200) {
        setExistingDocuments(response.data.data || []);
        setError('');
      }
    } catch (err) {
      console.error('Error fetching documents:', err);
      setError('Failed to fetch documents');
    }
  };

  const fetchPreviewUrl = async (uniqueKey) => {
    try {
      const response = await apiClient.get('/api/documents/presigned-url', {
        headers: {
          'unique-key': uniqueKey,
        },
      });
      const result = response.data;
      if (result.data) {
        window.open(result.data, '_blank');
      }
    } catch (error) {
      setError('Failed to fetch preview URL');
      console.error('Error fetching preview URL: ', error);
    }
  };

  const handleDeleteDocument = async (uniqueKey) => {
    try {
      await apiClient.delete('/api/documents/delete', {
        headers: {
          'unique-key': uniqueKey,
        },
      });

      const deletedDoc = existingDocuments.find(
        (doc) => doc.document_unique_key === uniqueKey,
      );

      fetchExistingDocuments();
      setExistingDocuments((prev) =>
        prev.filter((doc) => doc.document_unique_key !== uniqueKey),
      );

      if (deletedDoc) {
        setDocumentStates((prev) => ({
          ...prev,
          [deletedDoc.document_name]: null,
        }));
      }
    } catch (error) {
      const error_message = error?.response?.data?.error_message;
      console.error('Error deleting document: ', error_message);
      setError(error_message || 'Failed to delete document');
    }
  };

  const handleRemove = async (e) => {
    e.stopPropagation();
    setIsDeleting(true);

    if (existingDocument) {
      try {
        await handleDeleteDocument(existingDocument.document_unique_key);
      } catch (error) {
        console.error('Error deleting document:', error);
      } finally {
        setIsDeleting(false);
      }
      return;
    }

    // Reset document state for locally added files
    setDocumentStates((prev) => ({
      ...prev,
      [documentType]: null,
    }));

    setIsDeleting(false);
  };

  const handleFileClick = () => {
    if (existingDocument) {
      fetchPreviewUrl(existingDocument.document_unique_key);
    } else if (file) {
      const fileURL = URL.createObjectURL(file);
      window.open(fileURL, '_blank');
    }
  };

  return (
    <div key={id} className="font-roboto">
      <div className="relative group justify-center items-center flex flex-col">
        {file || existingDocument ? (
          <TooltipProvider>
            <Tooltip delayDuration={200}>
              <TooltipTrigger asChild>
                <div
                  className={`relative flex ${classNames === 'w-36 h-10' ? '' : 'flex-col'} justify-center items-center border border-lightPurple hover:border-lightPurple ease-in duration-150 hover:border-opacity-70 border-opacity-30 rounded-md cursor-pointer overflow-hidden ${classNames}`}
                  onClick={handleFileClick}
                >
                  <div className="w-full h-full flex items-center justify-center">
                    {file ? (
                      renderFilePreview(file)
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <FileText className="h-8 w-8 text-gray-400" />
                      </div>
                    )}
                  </div>
                  <span className="text-xs text-center p-2 truncate max-w-full absolute bottom-0 w-full bg-lightPurple bg-opacity-60 text-white backdrop-filter backdrop-blur-sm">
                    {existingDocument ? existingDocument.file_name : file.name}
                  </span>

                  {(file || existingDocument) && editState && (
                    <button
                      type="button"
                      onClick={handleRemove}
                      disabled={isDeleting}
                      className="absolute top-1 right-1 bg-purple-600 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity disabled:opacity-50"
                    >
                      {isDeleting ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Trash2 className="h-4 w-4" />
                      )}
                    </button>
                  )}
                </div>
              </TooltipTrigger>
              <TooltipContent className="bg-gray-100 bg-opacity-45 p-2 backdrop-filter backdrop-blur-sm rounded-md shadow-md translate-y-6">
                <p className="text-center text-sm text-gray-600">
                  Click to preview
                </p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        ) : (
          <div
            className={`border-2 border-dashed border-gray-400 rounded-md flex ${classNames === 'w-36 h-10' ? '' : 'flex-col'}  items-center justify-center cursor-pointer text-muted-foreground hover:text-darkPurple hover:border-darkerPurple ease-in duration-200 ${classNames}`}
            onClick={() => {
              setShowUploadModal(true);
              setSelectedDocument(existingDocument);
            }}
          >
            <Upload className="h-4 mx-2 w-4" />
            <span className="text-sm text-center">{label}</span>
          </div>
        )}
      </div>

      <UploadDocumentModal
        open={showUploadModal}
        onClose={() => {
          setShowUploadModal(false);
          setSelectedDocument(null);
        }}
        DOCUMENT_TITLES={DOCUMENT_TITLES}
        MAX_FILE_SIZE={MAX_FILE_SIZE}
        ACCEPTED_FILE_TYPES={ACCEPTED_FILE_TYPES}
        existingDocuments={existingDocuments}
        setExistingDocuments={setExistingDocuments}
        documentStates={documentStates}
        setDocumentStates={setDocumentStates}
        uploadProgress={uploadProgress}
        setUploadProgress={setUploadProgress}
        isUploading={isUploading}
        setIsUploading={setIsUploading}
        setError={setError}
        id={id}
        label={label}
        documentType={documentType}
        existingDocument={existingDocument}
        isUploadingCertificate={isUploadingCertificate}
        editState={editState}
      />
    </div>
  );
};

export default FileUpload;
